import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import './ConstructionRecordForm.css'
import './WellList.css'

const UserManagement = ({ isOpen, onClose, userRole }) => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // const [hasPermission, setHasPermission] = useState(false) // Unused
  const [currentUserEmail, setCurrentUserEmail] = useState('')

  // 載入用戶列表
  const fetchUsers = async () => {
    setLoading(true)
    setError('')

    try {
      if (!supabase) {
        throw new Error('Supabase 客戶端未正確初始化')
      }

      // 只從 user_permissions 表獲取用戶資料
      const { data: permissionsData, error: permissionsError } = await supabase
        .from('user_permissions')
        .select('*')
        .order('created_at', { ascending: false })

      if (permissionsError) {
        throw new Error('無法從權限表獲取用戶資料：' + permissionsError.message)
      }

      setUsers(permissionsData || [])

    } catch (error) {
      console.error('載入用戶列表失敗:', error)
      setError('載入用戶列表失敗：' + error.message)
    } finally {
      setLoading(false)
    }
  }



  // 獲取當前用戶郵箱
  const getCurrentUserEmail = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (session?.user?.email) {
        setCurrentUserEmail(session.user.email.toLowerCase())
      }
      if (sessionError) {
        console.error('獲取會話失敗:', sessionError)
      }
    } catch (err) {
      console.error('獲取當前用戶信息失敗:', err)
    }
  }

  useEffect(() => {
    if (isOpen) {
      // 先獲取當前用戶郵箱
      getCurrentUserEmail()

      const isAdmin = userRole === 'admin'
      // setHasPermission(isAdmin) // Removed unused state setter

      if (isAdmin) {
        fetchUsers()
      } else {
        setError('您沒有權限訪問用戶管理功能')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userRole])

  // 更新用戶權限
  const updateUserPermission = async (userId, email, newRole, isActive) => {
    try {
      if (currentUserEmail && email && email.toLowerCase() === currentUserEmail) {
        alert('安全限制：無法修改自己的權限設置\n\n為了系統安全，所有用戶（包括管理員）都無法修改自己的角色權限或啟用狀態。')
        return
      }

      if (!supabase) {
        throw new Error('Supabase 客戶端未正確初始化')
      }

      // 使用 UPDATE 而不是 UPSERT，避免唯一約束衝突
      const { error } = await supabase
        .from('user_permissions')
        .update({
          role: newRole,
          is_active: isActive,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('email', email.toLowerCase()) // 確保郵箱統一為小寫

      if (error) {
        throw error
      }

      // 重新載入用戶列表
      await fetchUsers()

      alert('用戶權限更新成功！')

    } catch (error) {
      console.error('更新用戶權限失敗:', error)
      alert('更新用戶權限失敗：' + error.message)
    }
  }

  // 刪除用戶
  const deleteUser = async (userId, email) => {
    // 安全檢查：防止刪除自己的帳號
    if (currentUserEmail && email && email.toLowerCase() === currentUserEmail) {
      alert('安全限制：無法刪除自己的帳號\n\n為了系統安全，所有用戶（包括管理員）都無法刪除自己的帳號。\n如需刪除此帳號，請聯繫其他管理員。')
      return
    }

    if (!window.confirm(`確定要刪除用戶 ${email} 嗎？`)) {
      return
    }

    try {
      if (!supabase) {
        throw new Error('Supabase 客戶端未正確初始化')
      }

      // 從權限表刪除
      const { error: permissionError } = await supabase
        .from('user_permissions')
        .delete()
        .eq('user_id', userId)
        .eq('email', email.toLowerCase()) // 確保郵箱統一為小寫

      if (permissionError) {
        console.warn('刪除權限記錄失敗:', permissionError)
      }

      // 嘗試刪除 Auth 用戶（需要 service_role key）
      try {
        const { error: authError } = await supabase.auth.admin.deleteUser(userId)
        if (authError) {
          console.warn('刪除認證用戶失敗:', authError)
        }
      } catch (authError) {
        console.warn('無法刪除認證用戶（需要 service_role 權限）:', authError)
      }

      // 重新載入用戶列表
      await fetchUsers()

      alert('用戶刪除成功！')

    } catch (error) {
      console.error('刪除用戶失敗:', error)
      alert('刪除用戶失敗：' + error.message)
    }
  }

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '未知'
    return new Date(dateString).toLocaleString('zh-TW')
  }

  // 角色選項
  const roleOptions = [
    { value: 'pending', label: '待審核', color: '#ffc107' },
    { value: 'viewer', label: '查看者', color: '#6c757d' },
    { value: 'editor', label: '編輯者', color: '#28a745' },
    { value: 'admin', label: '管理員', color: '#dc3545' }
  ]

  const getRoleColor = (role) => {
    const option = roleOptions.find(opt => opt.value === role)
    return option ? option.color : '#6c757d'
  }

  const getRoleLabel = (role) => {
    const option = roleOptions.find(opt => opt.value === role)
    return option ? option.label : role
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '900px', height: '80vh' }}>
        <div className="modal-header">
          <h2>用戶權限管理</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="construction-form" style={{ height: 'calc(100% - 80px)', overflow: 'auto' }}>
          {error && (
            <div className="error-message" style={{ margin: '20px 0' }}>
              {error}
              {error.includes('您沒有權限') && (
                <div style={{ marginTop: '15px', fontSize: '12px' }}>
                  <p><strong>權限檢查資訊：</strong></p>
                  <ul style={{ paddingLeft: '20px', marginBottom: '15px' }}>
                    <li>當前角色：{userRole || '未知'}</li>
                    <li>需要角色：admin（管理員）</li>
                    <li>是否已登入：{userRole ? '是' : '否'}</li>
                  </ul>

                  <p><strong>解決方案：</strong></p>
                  <ul style={{ paddingLeft: '20px' }}>
                    <li>確認您的帳號已被設為管理員角色</li>
                    <li>請管理員在用戶管理中將您的角色設為「admin」</li>
                    <li>設置完成後請重新登入</li>
                    <li>確認帳號已被啟用 (is_active = true)</li>
                  </ul>

                  {userRole && userRole !== 'admin' && (
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      background: '#fff3cd',
                      border: '1px solid #ffeaa7',
                      borderRadius: '4px',
                      fontSize: '11px'
                    }}>
                      <strong>提示：</strong> 您當前的角色是「{userRole}」，只有「admin」角色才能管理用戶。
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {!error && (
            <div className="form-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3>註冊用戶列表</h3>
                <button
                  className="add-btn"
                  onClick={fetchUsers}
                  disabled={loading}
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  {loading ? '載入中...' : '重新載入'}
                </button>
              </div>

              {loading && <div style={{ textAlign: 'center', padding: '20px' }}>載入中...</div>}

              {!loading && users.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                  尚無註冊用戶
                </div>
              )}

              {!loading && users.length > 0 && (
                <div style={{ display: 'grid', gap: '15px' }}>
                  {users.map((user, index) => {
                    // 檢查是否為當前登入用戶自己的帳號
                    const isSelfAccount = currentUserEmail && user.email &&
                      user.email.toLowerCase() === currentUserEmail

                    return (
                      <div key={user.user_id || index} style={{
                        border: isSelfAccount ? '2px solid #17a2b8' : '2px solid #e1e8ed',
                        borderRadius: '10px',
                        padding: '20px',
                        background: isSelfAccount ? '#e6f3ff' : 'white',
                        position: 'relative'
                      }}>
                        {/* 自己帳號標識 */}
                        {isSelfAccount && (
                          <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '15px',
                            background: '#17a2b8',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '10px',
                            fontWeight: '500'
                          }}>
                            您的帳號
                          </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px 120px', gap: '20px', alignItems: 'center' }}>
                          {/* 用戶資訊 */}
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '8px' }}>
                              {user.full_name}
                            </div>
                            <div style={{ color: '#6c757d', fontSize: '14px', marginBottom: '4px' }}>
                              {user.email}
                            </div>
                            <div style={{ color: '#6c757d', fontSize: '14px', marginBottom: '4px' }}>
                              {user.organization || '未提供'}
                            </div>
                            <div style={{ color: '#6c757d', fontSize: '12px' }}>
                              註冊時間：{formatDate(user.created_at)}
                            </div>
                            <div style={{
                              marginTop: '8px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              display: 'inline-block',
                              fontSize: '12px',
                              fontWeight: '500',
                              color: 'white',
                              background: getRoleColor(user.role)
                            }}>
                              {getRoleLabel(user.role)}
                            </div>
                          </div>

                          {/* 權限設定 */}
                          <div>
                            <div style={{ marginBottom: '10px' }}>
                              <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '5px', display: 'block' }}>
                                角色權限
                              </label>
                              <select
                                value={user.role}
                                onChange={(e) => updateUserPermission(user.user_id, user.email, e.target.value, user.is_active)}
                                disabled={isSelfAccount}
                                style={{
                                  width: '100%',
                                  padding: '6px 8px',
                                  borderRadius: '4px',
                                  border: '1px solid #ddd',
                                  fontSize: '14px',
                                  backgroundColor: isSelfAccount ? '#f8f9fa' : 'white',
                                  cursor: isSelfAccount ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {roleOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              {isSelfAccount && (
                                <div style={{
                                  fontSize: '10px',
                                  color: '#6c757d',
                                  marginTop: '2px'
                                }}>
                                  無法修改自己的角色權限
                                </div>
                              )}
                            </div>

                            <div>
                              <label style={{
                                fontSize: '12px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                cursor: isSelfAccount ? 'not-allowed' : 'pointer',
                                color: isSelfAccount ? '#6c757d' : 'inherit'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={user.is_active}
                                  onChange={(e) => updateUserPermission(user.user_id, user.email, user.role, e.target.checked)}
                                  disabled={isSelfAccount}
                                  style={{
                                    cursor: isSelfAccount ? 'not-allowed' : 'pointer'
                                  }}
                                />
                                啟用帳號
                              </label>
                              {isSelfAccount && (
                                <div style={{
                                  fontSize: '10px',
                                  color: '#6c757d',
                                  marginTop: '2px'
                                }}>
                                  無法修改自己的權限狀態
                                </div>
                              )}
                            </div>
                          </div>

                          {/* 操作按鈕 */}
                          <div>
                            <button
                              onClick={() => deleteUser(user.user_id, user.email)}
                              disabled={isSelfAccount}
                              style={{
                                background: isSelfAccount ? '#6c757d' : '#dc3545',
                                color: 'white',
                                border: 'none',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                cursor: isSelfAccount ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                width: '100%',
                                opacity: isSelfAccount ? 0.6 : 1
                              }}
                              title={isSelfAccount ? '無法刪除自己的帳號' : '刪除用戶'}
                            >
                              {isSelfAccount ? '無法刪除' : '刪除用戶'}
                            </button>
                            {isSelfAccount && (
                              <div style={{
                                fontSize: '10px',
                                color: '#6c757d',
                                textAlign: 'center',
                                marginTop: '4px'
                              }}>
                                無法刪除自己的帳號
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              <div style={{ marginTop: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>權限說明：</h4>
                <ul style={{ margin: '0 0 15px 0', paddingLeft: '20px', fontSize: '12px', color: '#6c757d' }}>
                  <li><strong>待審核：</strong>新註冊用戶，無編輯權限</li>
                  <li><strong>查看者：</strong>只能查看資料，無編輯權限</li>
                  <li><strong>編輯者：</strong>可以新增、編輯水井和施工記錄</li>
                  <li><strong>管理員：</strong>擁有所有權限，包括用戶管理</li>
                </ul>

                <div style={{
                  marginTop: '15px',
                  padding: '10px',
                  background: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '6px'
                }}>
                  <h5 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#856404' }}>安全限制：</h5>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '11px', color: '#856404' }}>
                    <li>所有用戶都無法修改自己的角色權限</li>
                    <li>所有用戶都無法修改自己的帳號啟用狀態</li>
                    <li>所有用戶都無法刪除自己的帳號</li>
                    <li>這些限制確保系統安全，防止權限濫用</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UserManagement 